<template>
    <div 
        class="multiform-step" 
        :id="'multiformSection' + sectionData.index"
        :class="{ 'multiform-step--active': sectionData.isVisible }"
    >
        <div class="multiform-step-header">
            <h3 class="multiform-step-title">
                <template v-if="!sectionData.isVisible">
                    <button 
                        type="button" 
                        class="multiform-step-title-clickable"
                        :title="'Edit Section ' + sectionData.heading"
                        :href="'#multiformSection' + sectionData.index"
                        @click.prevent="$emit('edit', sectionData.sectionName)"
                    >
                        {{ sectionData.index + 1 }}. {{ sectionData.heading }}
                    </button>
                </template>
                <template v-else>
                    {{ sectionData.index + 1 }}. {{ sectionData.heading }}
                </template>
            </h3>
            <div class="multiform-step-controls">
                <button 
                    type="button"
                    class="multiform-step-edit"
                    v-if="sectionData.isValid && !sectionData.isVisible"
                    :title="'Edit Section ' + sectionData.heading"
                    @click.prevent="$emit('edit', sectionData.sectionName)"
                >
                    Edit
                </button>
                <span class="multiform-step-complete">
                    <svg v-if="sectionData.isValid" width="32" height="23" viewBox="0 0 32 23" fill="none">
                        <path
                            d="M14.7461 20.5C13.7435 21.5026 12.5781 22.0039 11.25 22.0039C9.92188 22.0039 8.75651 21.5026 7.75391 20.5L0.742188 13.5078C0.247396 13 0 12.4141 0 11.75C0 11.0859 0.253906 10.5 0.761719 9.99219C1.25651 9.4974 1.83594 9.25 2.5 9.25C3.16406 9.25 3.75 9.4974 4.25781 9.99219L11.25 17.0039L26.9922 1.24219C27.5 0.747396 28.0859 0.5 28.75 0.5C29.4141 0.5 30 0.747396 30.5078 1.24219C31.0026 1.75 31.25 2.33594 31.25 3C31.25 3.66406 31.0026 4.25 30.5078 4.75781L14.7461 20.5Z"
                            fill="#D0D0CE" />
                    </svg>
                </span>
            </div>
        </div>
        <transition 
            name="slide" 
            :css="false" 
            @before-enter="beforeEnter" 
            @enter="enter"
            @after-enter="afterEnter" 
            @before-leave="beforeLeave" 
            @leave="leave"
            @after-leave="afterLeave"
        >
            <div 
                class="multiform-step-body-wrapper" 
                v-show="sectionData.isVisible"
                :aria-hidden="!sectionData.isVisible"
            >
                <div class="multiform-step-body" ref="body">
                    <slot name="default" :sectionData="sectionData"></slot>
                    <div v-if="showAdvanceButton">
                        <button
                            type="button"
                            class="btn med rad btn01"
                            :disabled="!sectionData.isValid"
                            @click.prevent="$emit('advance', sectionData.sectionName)"
                        >
                            {{ sectionData.isValid ? 'Next' : 'Please Complete All Fields' }}
                        </button>
                    </div>
                    <div v-if="showFinishButton">
                        <button
                            type="button"
                            class="btn med rad btn01"
                            :disabled="!sectionData.isValid || isLocked"
                            @click.prevent="$emit('finishForm', {})"
                        >
                            {{
                                isLocked
                                ? 'Submitting Registration...'
                                : sectionData.isValid
                                    ? 'Submit Registration'
                                    : 'Please Complete All Fields'
                            }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
// Animating to height: 'auto' isn't possible with only CSS,
// so we're loading Velocity in to help us out.
import 'velocity-animate';

export default {
    name: 'Section',
    props: {
        sectionData: {
            type: Object,
            required: true
        },
        showAdvanceButton: {
            type: Boolean,
            default: true
        },
        showFinishButton: {
            type: Boolean,
            default: false
        },
        isLocked: {
            type: Boolean,
            default: false
        }
    },
    methods: {
        beforeEnter(el) {
            el.style.height = '0px';
        },
        enter(el, done) {
            /* Wait for the currently open panel's leave transition to complete before animating */
            setTimeout(() => {
                let newHeight = this.$refs.body.offsetHeight + 'px';
                Velocity(el, 'scroll', {
                    duration: 300,
                    easing: 'ease-in-out',
                    offset: -210,
                }
                );
                Velocity(el, {
                    height: newHeight
                },
                    {
                        duration: 800,
                        easing: 'ease-in-out'
                    }
                );
                Velocity(el, 'scroll', {
                    duration: 800,
                    easing: 'ease-in-out',
                    offset: -210,
                    complete: done
                }
                );
            }, 800);
        },
        afterEnter(el) {
            /*
             * We animate to a specific height, but we set the height
             * back to 'auto' here, to allow the form to change height
             * as validation errors appear and disappear.
             */
            setTimeout(() => {
                el.style.height = 'auto';
                this.$emit('afterSectionOpen');
            }, 100);
        },
        beforeLeave(el) {
            let currentHeight = el.offsetHeight + 'px';
            el.style.height = currentHeight;
        },
        leave(el, done) {
            Velocity(el, {
                height: '0px'
            },
                {
                    duration: 800,
                    easing: 'ease-in-out',
                    complete: done
                }
            );
        },
        afterLeave(el) {
            setTimeout(() => {
                el.style.height = 'auto';
            }, 100);
        }
    }
}
</script>

<style></style>
